/**
 * NSG theme JS: no frameworks, no build step.
 * - Product page: keep the variant id, price, add-to-cart button, URL and gallery in sync with
 *   the selected color/size radios.
 * - Gallery: thumbnails scroll the swipeable gallery; tapping an image opens the zoom window
 *   (a <dialog>: click the image to zoom 2x where you clicked, prev/next, Esc or backdrop closes).
 * - Home collection rows: chrome arrow bubbles scroll the row by about a screen; they hide at the
 *   ends and when the row fits (swiping is native scroll + scroll snap).
 * - Cart: +/- quantity buttons that submit the cart form.
 * - Footer: the fake "active visitors" odometer starts on an angel number and drifts up or down
 *   by a little every 1-4 s, pulled back toward where it started so it never wanders far.
 */
(() => {
  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2);
    const [whole, dec] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
      switch (kind) {
        case 'amount_no_decimals': return withCommas;
        case 'amount_with_comma_separator': return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`;
        case 'amount_no_decimals_with_comma_separator': return whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        default: return `${withCommas}.${dec}`;
      }
    });
  };

  const scrollGalleryTo = (gallery, mediaId) => {
    const slide = gallery && gallery.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;
    gallery.scrollTo({ left: slide.offsetLeft - gallery.offsetLeft, behavior: 'smooth' });
    document.querySelectorAll('[data-thumb]').forEach((t) => t.setAttribute('aria-current', String(t.dataset.thumb === String(mediaId))));
  };

  function initProduct(root) {
    const form = root.querySelector('[data-product-form]');
    const data = root.querySelector('[data-variants]');
    if (!form || !data) return;
    const variants = JSON.parse(data.textContent);
    const idInput = form.querySelector('[data-variant-id]');
    const button = form.querySelector('[data-add-to-cart]');
    const buyNow = form.querySelector('[data-buy-now]');
    const price = root.querySelector('.product__price [data-price]');
    const gallery = root.querySelector('[data-gallery]');
    const fieldsets = [...form.querySelectorAll('[data-option]')];

    const read = () => fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
    const matches = (v, selected, skip) => v.options.every((opt, j) => j === skip || selected[j] === undefined || opt === selected[j]);
    // Cross out each value that has no in-stock variant with the other current choices.
    const refresh = (selected) => fieldsets.forEach((fs, i) => fs.querySelectorAll('input').forEach((input) => {
      const ok = variants.some((v) => v.available && v.options[i] === input.value && matches(v, selected, i));
      input.nextElementSibling.classList.toggle('is-unavailable', !ok);
    }));

    form.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"]')) return;
      const selected = read();
      const variant = variants.find((v) => matches(v, selected));
      fieldsets.forEach((fs, i) => { fs.querySelector('[data-option-value]').textContent = selected[i] || ''; });
      refresh(selected);

      if (!variant) {
        // Never switch the shopper's other choices for them; just say it's unavailable, and still
        // show a photo of the value they picked (e.g. the new color).
        button.disabled = true;
        button.textContent = button.dataset.labelUnavailable;
        if (buyNow) buyNow.hidden = true;
        const k = fieldsets.indexOf(event.target.closest('[data-option]'));
        const look = variants.find((v) => v.featured_media && v.options[k] === selected[k]);
        if (look) scrollGalleryTo(gallery, look.featured_media.id);
        return;
      }
      idInput.value = variant.id;
      button.disabled = !variant.available;
      if (buyNow) buyNow.hidden = !variant.available;
      button.textContent = variant.available ? button.dataset.labelAdd : button.dataset.labelSold;
      if (price) price.textContent = formatMoney(variant.price, form.dataset.moneyFormat);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      if (variant.featured_media) scrollGalleryTo(gallery, variant.featured_media.id);
    });

    refresh(read());

    root.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => scrollGalleryTo(gallery, thumb.dataset.thumb));
    });
  }

  function initZoom(root) {
    const dialog = root.querySelector('[data-zoom-dialog]');
    if (!dialog || !dialog.showModal) return;
    const body = dialog.querySelector('[data-zoom-body]');
    const img = dialog.querySelector('[data-zoom-img]');
    const count = dialog.querySelector('[data-zoom-count]');
    const slides = [...root.querySelectorAll('[data-zoom-src]')];
    let index = 0;
    dialog.querySelector('[data-zoom-nav]').hidden = slides.length < 2;

    const show = (n) => {
      index = (n + slides.length) % slides.length;
      body.classList.remove('is-zoomed');
      img.src = slides[index].dataset.zoomSrc;
      img.alt = slides[index].dataset.zoomAlt || '';
      count.textContent = `${index + 1} / ${slides.length}`;
    };

    root.addEventListener('click', (event) => {
      const slide = event.target.closest('[data-zoom-src]');
      if (!slide) return;
      show(slides.indexOf(slide));
      dialog.showModal();
    });
    img.addEventListener('click', (event) => {
      const zooming = !body.classList.contains('is-zoomed');
      const rx = event.offsetX / img.clientWidth;
      const ry = event.offsetY / img.clientHeight;
      body.classList.toggle('is-zoomed', zooming);
      if (zooming) {
        body.scrollLeft = rx * img.clientWidth - body.clientWidth / 2;
        body.scrollTop = ry * img.clientHeight - body.clientHeight / 2;
      }
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close(); // backdrop
      const step = event.target.closest('[data-zoom-step]');
      if (step) show(index + Number(step.dataset.zoomStep));
      if (event.target.closest('[data-zoom-close]')) dialog.close();
    });
    dialog.addEventListener('keydown', (event) => {
      if (slides.length < 2) return;
      if (event.key === 'ArrowLeft') show(index - 1);
      if (event.key === 'ArrowRight') show(index + 1);
    });
  }

  function initRow(row) {
    const track = row.querySelector('[data-row-track]');
    const [prev, next] = row.querySelectorAll('[data-row-step]');
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      row.classList.toggle('is-scrollable', max > 2);
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 2;
    };
    row.addEventListener('click', (event) => {
      const step = event.target.closest('[data-row-step]');
      if (step) track.scrollBy({ left: Number(step.dataset.rowStep) * track.clientWidth * 0.9, behavior: 'smooth' });
    });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  function initCart() {
    const form = document.querySelector('[data-cart-form]');
    if (!form) return;
    form.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-qty-step]');
      if (!btn) return;
      const input = btn.closest('[data-qty]').querySelector('input');
      input.value = Math.max(0, (parseInt(input.value, 10) || 0) + Number(btn.dataset.qtyStep));
      form.requestSubmit();
    });
    form.addEventListener('change', (event) => {
      if (event.target.matches('.qty__input')) form.requestSubmit();
    });
  }

  function initCounter() {
    const odo = document.querySelector('[data-odometer]');
    if (!odo) return;
    const cells = [...odo.children];
    const starts = [69, 67, 111, 222, 333, 420, 444, 555, 666, 777, 888, 999, 1111];
    const start = starts[(Math.random() * starts.length) | 0];
    const range = Math.max(6, Math.round(start * 0.08));
    let count = start;
    const show = () => {
      const digits = String(count).padStart(cells.length, '0').slice(-cells.length);
      cells.forEach((cell, i) => {
        if (cell.textContent === digits[i]) return;
        cell.textContent = digits[i];
        cell.classList.remove('is-ticking');
        void cell.offsetWidth; // restart the tick animation
        cell.classList.add('is-ticking');
      });
    };
    const tick = () => {
      if (!document.hidden) {
        // More likely to go up when below the start, down when above it.
        const up = Math.random() < 0.5 - (count - start) / (2 * range);
        const step = Math.random() < 0.8 ? 1 : 2;
        count = Math.max(1, count + (up ? step : -step));
        show();
      }
      setTimeout(tick, 1000 + Math.random() * 3000);
    };
    show();
    setTimeout(tick, 1000 + Math.random() * 3000);
  }

  document.querySelectorAll('[data-product]').forEach((root) => { initProduct(root); initZoom(root); });
  document.querySelectorAll('[data-row]').forEach(initRow);
  initCart();
  initCounter();
})();
